/**
 * CapabilityRequirementsEditor
 *
 * Allows specifying modem capability requirements for primitives and scenarios.
 * These requirements are used for smart modem allocation at runtime.
 */

import { useState, useMemo } from 'react';
import { Button, Card, Badge, Input } from '@/components/ui';
import { Label } from '@/components/ui/Label';
import { Switch } from '@/components/ui/Switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  ChevronDown,
  Signal,
  Zap,
  Wifi,
  Activity,
  Settings,
  Plus,
  Trash2,
  CheckCircle,
  Star,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  STANDARD_CAPABILITIES,
  getCapabilitiesByCategory,
  type CapabilityRequirement,
  type PrimitiveCapabilityRequirements,
  type CapabilityDefinition,
} from '@simcheck/shared-types';

interface CapabilityRequirementsEditorProps {
  requirements: PrimitiveCapabilityRequirements;
  onChange: (requirements: PrimitiveCapabilityRequirements) => void;
  readOnly?: boolean;
}

// Category icons
const CATEGORY_ICONS: Record<string, typeof Signal> = {
  network: Signal,
  features: Zap,
  iot: Wifi,
  performance: Activity,
};

// Category display names
const CATEGORY_NAMES: Record<string, string> = {
  network: 'Network Technology',
  features: 'Advanced Features',
  iot: 'IoT Capabilities',
  performance: 'Performance',
};

// Get unique categories from capabilities
const CATEGORIES = [...new Set(STANDARD_CAPABILITIES.map((c) => c.category))];

export function CapabilityRequirementsEditor({
  requirements,
  onChange,
  readOnly = false,
}: CapabilityRequirementsEditorProps) {
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['network']);

  // Helper to find if a capability is already required or preferred
  const findRequirement = (capabilityId: string): { requirement: CapabilityRequirement | null; type: 'required' | 'preferred' | null } => {
    const inRequired = requirements.required?.find((r) => r.capabilityId === capabilityId);
    if (inRequired) return { requirement: inRequired, type: 'required' };
    const inPreferred = requirements.preferred?.find((r) => r.capabilityId === capabilityId);
    if (inPreferred) return { requirement: inPreferred, type: 'preferred' };
    return { requirement: null, type: null };
  };

  // Add a capability requirement
  const addRequirement = (capabilityId: string, type: 'required' | 'preferred') => {
    const definition = STANDARD_CAPABILITIES.find((c) => c.id === capabilityId);
    if (!definition) return;

    const newRequirement: CapabilityRequirement = {
      capabilityId,
      required: type === 'required',
      value: definition.type === 'boolean' ? true : undefined,
      operator: definition.type === 'number' ? 'gte' : undefined,
      numericValue: definition.type === 'number' ? (definition.defaultValue as number) || 0 : undefined,
      arrayValues: definition.type === 'array' ? [] : undefined,
      arrayMatchMode: definition.type === 'array' ? 'all' : undefined,
    };

    const newRequirements = { ...requirements };
    if (type === 'required') {
      newRequirements.required = [...(requirements.required || []), newRequirement];
      // Remove from preferred if exists
      newRequirements.preferred = (requirements.preferred || []).filter(
        (r) => r.capabilityId !== capabilityId
      );
    } else {
      newRequirements.preferred = [...(requirements.preferred || []), newRequirement];
      // Remove from required if exists
      newRequirements.required = (requirements.required || []).filter(
        (r) => r.capabilityId !== capabilityId
      );
    }
    onChange(newRequirements);
  };

  // Remove a capability requirement
  const removeRequirement = (capabilityId: string) => {
    const newRequirements = {
      ...requirements,
      required: (requirements.required || []).filter((r) => r.capabilityId !== capabilityId),
      preferred: (requirements.preferred || []).filter((r) => r.capabilityId !== capabilityId),
    };
    onChange(newRequirements);
  };

  // Update a requirement's value
  const updateRequirement = (
    capabilityId: string,
    updates: Partial<CapabilityRequirement>
  ) => {
    const { type } = findRequirement(capabilityId);
    if (!type) return;

    const newRequirements = { ...requirements };
    const list = type === 'required' ? 'required' : 'preferred';
    newRequirements[list] = (requirements[list] || []).map((r) =>
      r.capabilityId === capabilityId ? { ...r, ...updates } : r
    );
    onChange(newRequirements);
  };

  // Toggle category expansion
  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  // Count requirements by category
  const countByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const category of CATEGORIES) {
      const caps = getCapabilitiesByCategory(category);
      counts[category] = caps.filter(
        (c) => findRequirement(c.id).type !== null
      ).length;
    }
    return counts;
  }, [requirements]);

  // Render a single capability item
  const renderCapabilityItem = (definition: CapabilityDefinition) => {
    const { requirement, type } = findRequirement(definition.id);
    const isActive = type !== null;
    const Icon = CATEGORY_ICONS[definition.category] || Settings;

    return (
      <div
        key={definition.id}
        className={cn(
          'flex items-start gap-3 p-3 rounded-lg border transition-colors',
          isActive
            ? 'border-accent-blue/50 bg-accent-blue/5'
            : 'border-border hover:bg-muted/50'
        )}
      >
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground">{definition.displayName}</span>
            {definition.type === 'number' && definition.unit && (
              <Badge variant="secondary" className="text-xs">
                {definition.unit}
              </Badge>
            )}
            {isActive && (
              <Badge
                variant={type === 'required' ? 'default' : 'secondary'}
                className="text-xs"
              >
                {type === 'required' ? (
                  <><CheckCircle className="w-3 h-3 mr-1" /> Required</>
                ) : (
                  <><Star className="w-3 h-3 mr-1" /> Preferred</>
                )}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{definition.description}</p>

          {/* Value editor for active requirements */}
          {isActive && requirement && (
            <div className="flex items-center gap-2 pt-2">
              {definition.type === 'number' && (
                <>
                  <Select
                    value={requirement.operator || 'gte'}
                    onValueChange={(v) =>
                      updateRequirement(definition.id, {
                        operator: v as 'eq' | 'gt' | 'gte' | 'lt' | 'lte',
                      })
                    }
                    disabled={readOnly}
                  >
                    <SelectTrigger className="w-20 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="eq">=</SelectItem>
                      <SelectItem value="gt">&gt;</SelectItem>
                      <SelectItem value="gte">&ge;</SelectItem>
                      <SelectItem value="lt">&lt;</SelectItem>
                      <SelectItem value="lte">&le;</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    value={requirement.numericValue || 0}
                    onChange={(e) =>
                      updateRequirement(definition.id, {
                        numericValue: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-24 h-8"
                    disabled={readOnly}
                  />
                  {definition.unit && (
                    <span className="text-sm text-muted-foreground">{definition.unit}</span>
                  )}
                </>
              )}
              {definition.type === 'enum' && definition.options && (
                <Select
                  value={String(requirement.stringValue || definition.defaultValue)}
                  onValueChange={(v) =>
                    updateRequirement(definition.id, { stringValue: v })
                  }
                  disabled={readOnly}
                >
                  <SelectTrigger className="w-32 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {definition.options.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {definition.type === 'array' && (
                <div className="flex items-center gap-2 flex-1">
                  <Select
                    value={requirement.arrayMatchMode || 'all'}
                    onValueChange={(v) =>
                      updateRequirement(definition.id, {
                        arrayMatchMode: v as 'any' | 'all',
                      })
                    }
                    disabled={readOnly}
                  >
                    <SelectTrigger className="w-28 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">has all of</SelectItem>
                      <SelectItem value="any">has any of</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="text"
                    defaultValue={(requirement.arrayValues || []).join(', ')}
                    onBlur={(e) => {
                      const parsed = Array.from(
                        new Set(
                          e.target.value
                            .split(/[,\n]/)
                            .map((s) => s.trim())
                            .filter(Boolean)
                        )
                      );
                      updateRequirement(definition.id, { arrayValues: parsed });
                    }}
                    className="h-8 flex-1"
                    placeholder={
                      definition.options?.slice(0, 3).join(', ') || 'B1, B3, B20'
                    }
                    disabled={readOnly}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action buttons */}
        {!readOnly && (
          <div className="flex flex-col gap-1">
            {!isActive ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => addRequirement(definition.id, 'required')}
                  className="h-7 px-2 text-xs"
                  title="Add as required"
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Required
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => addRequirement(definition.id, 'preferred')}
                  className="h-7 px-2 text-xs"
                  title="Add as preferred"
                >
                  <Star className="w-3 h-3 mr-1" />
                  Preferred
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    addRequirement(
                      definition.id,
                      type === 'required' ? 'preferred' : 'required'
                    )
                  }
                  className="h-7 px-2 text-xs"
                  title={type === 'required' ? 'Change to preferred' : 'Change to required'}
                >
                  {type === 'required' ? (
                    <><Star className="w-3 h-3 mr-1" /> Preferred</>
                  ) : (
                    <><CheckCircle className="w-3 h-3 mr-1" /> Required</>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeRequirement(definition.id)}
                  className="h-7 px-2 text-xs text-status-error hover:text-status-error"
                  title="Remove requirement"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Remove
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const totalRequirements =
    (requirements.required?.length || 0) + (requirements.preferred?.length || 0);

  return (
    <div className="space-y-4">
      {/* Header with summary */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-foreground">Capability Requirements</h3>
          <p className="text-sm text-muted-foreground">
            Specify modem capabilities needed for this flow
          </p>
        </div>
        {totalRequirements > 0 && (
          <div className="flex items-center gap-2">
            <Badge variant="default">
              {requirements.required?.length || 0} required
            </Badge>
            <Badge variant="secondary">
              {requirements.preferred?.length || 0} preferred
            </Badge>
          </div>
        )}
      </div>

      {/* Description input */}
      <div>
        <Label htmlFor="requirements-description">Description (optional)</Label>
        <Input
          id="requirements-description"
          value={requirements.description || ''}
          onChange={(e) =>
            onChange({ ...requirements, description: e.target.value })
          }
          placeholder="e.g., Requires 5G modem for speed testing"
          disabled={readOnly}
        />
      </div>

      {/* Modem count */}
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Label htmlFor="modem-count">Minimum Modems Needed</Label>
          <Input
            id="modem-count"
            type="number"
            min={1}
            max={10}
            value={requirements.modemCount || 1}
            onChange={(e) =>
              onChange({
                ...requirements,
                modemCount: parseInt(e.target.value) || 1,
              })
            }
            className="w-24"
            disabled={readOnly}
          />
        </div>
      </div>

      {/* Categories with capabilities */}
      <div className="space-y-2">
        {CATEGORIES.map((category) => {
          const Icon = CATEGORY_ICONS[category] || Settings;
          const caps = getCapabilitiesByCategory(category).sort(
            (a, b) => (a.order || 0) - (b.order || 0)
          );
          const count = countByCategory[category];
          const isExpanded = expandedCategories.includes(category);

          return (
            <Collapsible
              key={category}
              open={isExpanded}
              onOpenChange={() => toggleCategory(category)}
            >
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-between h-10 px-3"
                >
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-muted-foreground" />
                    <span>{CATEGORY_NAMES[category] || category}</span>
                    {count > 0 && (
                      <Badge variant="secondary" className="ml-2">
                        {count}
                      </Badge>
                    )}
                  </div>
                  <ChevronDown
                    className={cn(
                      'w-4 h-4 text-muted-foreground transition-transform',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-2 pt-2 pl-2">
                {caps.map(renderCapabilityItem)}
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>

      {/* Empty state */}
      {totalRequirements === 0 && (
        <div className="text-center py-6 text-muted-foreground">
          <Settings className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No capability requirements specified</p>
          <p className="text-xs mt-1">
            Add requirements to ensure modems with specific capabilities are allocated
          </p>
        </div>
      )}
    </div>
  );
}
